import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  afterNextRender,
  computed,
  inject,
  input,
} from '@angular/core';
import { MeshGradient, type MeshGradientColorsConfig } from '@mesh-gradient/core';

/** Matches `$ai-color-1` / `$ai-color-2` in `packages/scss-assets`. */
const DEFAULT_MESH_COLORS: MeshGradientColorsConfig = [
  '#505ED7',
  '#77CEC7',
  '#505ED7',
  '#77CEC7',
];

@Component({
  selector: 'app-mesh-gradient',
  template: `
    <div class="mesh-gradient-bleed" [style.inset]="bleedInset()">
      <canvas class="mesh-gradient-canvas" aria-hidden="true"></canvas>
      <div
        class="mesh-gradient-grain"
        aria-hidden="true"
        [style.opacity]="grainOpacity()"
        [style.backgroundImage]="grainBackgroundImage()"
      ></div>
    </div>
  `,
  styleUrl: './mesh-gradient.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'mesh-gradient-host',
  },
})
export class MeshGradientComponent implements OnDestroy {
  private readonly host = inject(ElementRef<HTMLElement>);
  private mesh: MeshGradient | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private initRafId: number | null = null;

  /** Mesh animation speed. */
  readonly speed = input<number>(0.85);
  /** Mesh RNG seed. */
  readonly seed = input<number>(42);
  /** Mesh frequency. */
  readonly frequency = input<number | null>(null);
  /** Canvas blur (px). */
  readonly blurPx = input<number>(10);

  readonly colors = input<MeshGradientColorsConfig>(DEFAULT_MESH_COLORS);

  /** Grain opacity in percent (0..100). */
  readonly grainOpacityPercent = input<number>(42);
  /** Grain baseFrequency for SVG turbulence. */
  readonly grainFrequency = input<number>(0.65);
  /** Number of octaves for SVG turbulence. */
  readonly grainOctaves = input<number>(3);

  /** Bleed beyond bounds in percent (e.g. 10 -> inset:-10%). */
  readonly bleedPercent = input<number>(10);

  readonly bleedInset = computed(() => `-${this.bleedPercent()}%`);
  readonly grainOpacity = computed(() =>
    Math.min(1, Math.max(0, this.grainOpacityPercent() / 100)),
  );

  readonly grainBackgroundImage = computed(() => {
    const frequency = this.grainFrequency();
    const octaves = this.grainOctaves();

    // SVG is URI-encoded; baseFrequency and numOctaves are injected as plain numbers.
    const svg = `%3Csvg viewBox='0 0 2482 2482' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='${frequency}' numOctaves='${octaves}' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E`;
    return `url("data:image/svg+xml,${svg}")`;
  });

  readonly canvasFilter = computed(() => `blur(${this.blurPx()}px)`);

  constructor() {
    afterNextRender(() => {
      this.tryInitializeMesh();
      this.resizeObserver = new ResizeObserver(() => {
        this.tryInitializeMesh();
      });
      this.resizeObserver.observe(this.host.nativeElement);
    });
  }

  private tryInitializeMesh(): void {
    if (this.mesh) {
      return;
    }

    if (this.initRafId !== null) {
      cancelAnimationFrame(this.initRafId);
    }

    // Wait for the next frame to avoid initializing while the host is still 0x0.
    this.initRafId = requestAnimationFrame(() => {
      const hostElement = this.host.nativeElement;
      const canvas = hostElement.querySelector('canvas');
      if (!canvas) {
        return;
      }

      const { width, height } = hostElement.getBoundingClientRect();
      if (width <= 1 || height <= 1) {
        return;
      }

      canvas.style.filter = this.canvasFilter();

      const gradient = new MeshGradient();
      gradient.init(canvas, {
        colors: this.colors(),
        animationSpeed: this.speed(),
        seed: this.seed(),
        frequency: this.frequency() ?? { x: 0.00014, y: 0.00029, delta: 0.0001 },
      });
      this.mesh = gradient;
      this.initRafId = null;
    });
  }

  ngOnDestroy(): void {
    if (this.initRafId !== null) {
      cancelAnimationFrame(this.initRafId);
      this.initRafId = null;
    }
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.mesh?.destroy();
    this.mesh = null;
  }
}

